import {searchFetchContactable} from "./search-fetch-contactable.js";
import {searchFetchContact} from "./search-fetch-contact.js";
import PlzInput from "./PlzInput.js";
import PlzInputObserver from "./PlzInputObserver.js";
export default class PlzSearch {
  constructor() {
    this.inputs = [];
    this.currentInput = "";
    this.minIndex = 0;
    this.maxIndex = 4;
    this.nextIndex = this.minIndex;
    this.inputObserver = new PlzInputObserver(this.handleInputEvent.bind(this));
    try {
      this.plzSearchContainer = this.ensureElement(document.querySelector(".plz__search"), HTMLElement);
      this.plzFormContainer = this.ensureElement(document.querySelector(".plz__form"), HTMLElement);
      this.plzSuggestionsContainer = this.ensureElement(document.querySelector(".plz__suggestions"), HTMLElement);
      this.plzResultContainer = this.ensureElement(document.querySelector(".plz__result"), HTMLElement);
      this.plzResultContainerInner = this.ensureElement(this.plzResultContainer.querySelector(".plz__result-result"), HTMLElement);
      this.mirrorField = this.ensureElement(document.getElementById("plz-input-mirror__input"), HTMLInputElement);
    } catch {
      console.error("Failed to resolve locators. Disabling PLZ input field component.");
      return;
    }
    this.searchUrl = this.plzSearchContainer.getAttribute("data-search-url") || "";
    this.contactUrl = this.plzSearchContainer.getAttribute("data-contact-url") || "";
    this.init();
  }
  init() {
    this.inputs = this.initInputs();
    this.setCurrentInput(this.getCurrentInput());
    document.querySelectorAll(".plz__back-link").forEach((button) => {
      button.addEventListener("click", () => {
        this.reset();
      });
    });
    this.mirrorField.addEventListener("input", () => {
      this.setCurrentInput(this.mirrorField.value);
    });
  }
  initInputs() {
    const inputs = [];
    document.querySelectorAll(".plz__input").forEach((inputField) => {
      if (inputField instanceof HTMLInputElement) {
        try {
          const input = new PlzInput(inputField, this.getNextIndex());
          input.addObserver(this.inputObserver);
          inputs.push(input);
        } catch {
          return inputs;
        }
      }
    });
    return inputs;
  }
  getCurrentInput() {
    let currentZip = "";
    this.inputs.forEach((item) => {
      currentZip = currentZip + item.getValue();
    });
    return currentZip;
  }
  setCurrentInput(input) {
    input = this.truncateString(input, 5);
    input.split("").forEach((char, index) => {
      const targetInput = this.getInputAtPosition(index);
      targetInput?.setValue(char, false);
    });
  }
  handleInputEvent(eventName, input) {
    switch (eventName) {
      case "change":
        let nextPos = input.position + 1;
        this.inputs.filter((i) => i.position < input.position).some((el) => {
          if (el.isEmpty()) {
            el.setValue(input.getValue());
            input.clear();
            nextPos = el.position + 1;
            return true;
          }
        });
        this.setActive(nextPos, false);
        this.hasChanged();
        break;
      case "back":
        this.setActive(input.position - 1, true);
        this.hasChanged();
        break;
      case "clear":
        this.hasChanged();
        break;
      case "next":
        if (input.isEmpty())
          break;
        this.setActive(input.position + 1, false);
        break;
      case "prev":
        this.setActive(input.position - 1, false);
        break;
      default:
        break;
    }
  }
  hasChanged() {
    this.currentInput = this.getCurrentInput();
    this.mirrorField.value = this.currentInput;
    this.updateSuggestionList();
  }
  setActive(position, clearOnActivation = false) {
    position = Math.max(this.minIndex, Math.min(this.maxIndex, position));
    const target = this.getInputAtPosition(position);
    if (clearOnActivation)
      target?.clear();
    target?.focus();
  }
  getInputAtPosition(position) {
    return this.inputs.find((input) => input.position === position);
  }
  updateSuggestionList() {
    const timestamp = Date.now();
    this.suggestionRequestID = timestamp;
    const currentZip = this.getCurrentInput();
    if (currentZip === "") {
      this.clearSuggestions();
      return;
    }
    searchFetchContactable(this.searchUrl, currentZip).then((suggestions) => {
      if (this.suggestionRequestID !== timestamp)
        return;
      this.clearSuggestions();
      if (suggestions.length === 0) {
        const listItem = this.createNoResultsListItem();
        this.plzSuggestionsContainer.appendChild(listItem);
      } else {
        suggestions.forEach((suggestion) => {
          const listItem = this.createSuggestionListItem(suggestion);
          this.plzSuggestionsContainer.appendChild(listItem);
        });
      }
      this.hideContainer(this.plzResultContainer);
      this.showContainer(this.plzFormContainer);
      this.showContainer(this.plzSuggestionsContainer);
    });
  }
  createNoResultsListItem() {
    const listItem = document.createElement("li");
    listItem.textContent = this.plzSuggestionsContainer.getAttribute("data-noresult") || "";
    listItem.classList.add("font--highlighted");
    return listItem;
  }
  createSuggestionListItem(suggestion) {
    const listItem = document.createElement("li");
    listItem.textContent = `${suggestion.zipCode} - ${suggestion.municipality}`;
    listItem.classList.add("plz__suggestions-item", "font--highlighted");
    listItem.addEventListener("click", () => this.getContact(suggestion.contact.uid, suggestion.zipCode));
    return listItem;
  }
  getContact(uid, zipCode) {
    this.clearSuggestions();
    searchFetchContact(this.contactUrl, uid).then((result) => {
      this.setResult(result);
      this.plzResultContainer.querySelector(".zip-code").innerHTML = zipCode;
      this.showContainer(this.plzResultContainer);
      this.hideContainer(this.plzFormContainer);
    });
  }
  getContactPart(contactPart, special = "") {
    if (contactPart) {
      switch (special) {
        case "email":
          return `<a href="mailto:${contactPart}">${contactPart}</a><br/>`;
        case "phone":
          return `<a href="tel:${contactPart}">${contactPart}</a><br/>`;
        default:
          return `${contactPart} <br/>`;
      }
    }
    return "";
  }
  getContactLink(url, label) {
    return url && label ? `<a href="${url}" target="_blank" rel="noopener" class="button button--magenta button--inline margin-bottom--xs">${label}</a><br/>` : "";
  }
  clearSuggestions() {
    this.plzSuggestionsContainer.replaceChildren();
    this.hideContainer(this.plzSuggestionsContainer);
  }
  setResult(result) {
    this.plzResultContainerInner.innerHTML = `
        <p class="font--highlighted">
          ${this.getContactPart(result.company1)}
          ${this.getContactPart(result.company2)}
          ${this.getContactPart(result.address)}
          ${this.getContactPart(result.email, "email")}
          ${this.getContactPart(result.phone, "phone")}
        </p>
        <p class="margin-top--m">
          ${this.getContactLink(result.link1, result.link1Label)}
          ${this.getContactLink(result.link2, result.link2Label)}
        </p>
      `;
  }
  clearResultPage() {
    this.plzResultContainerInner.innerHTML = "";
  }
  reset() {
    this.inputs.forEach((input) => {
      input.clear();
    });
    this.showContainer(this.plzFormContainer);
    this.hideContainer(this.plzResultContainer);
  }
  ensureElement(elem, expectedType) {
    if (!elem) {
      throw new Error();
    }
    if (expectedType && !(elem instanceof expectedType)) {
      throw new Error();
    }
    return elem;
  }
  getNextIndex() {
    const index = this.nextIndex;
    if (index > this.maxIndex) {
      throw Error("PlzSearch: Maximum amount of indexes reached.");
    }
    this.nextIndex++;
    return index;
  }
  truncateString(value, maxLength) {
    return value.length > maxLength ? value.slice(0, maxLength) : value;
  }
  showContainer(container, show = true) {
    if (show) {
      container.classList.remove("hidden");
      container.setAttribute("aria-hidden", "false");
    } else {
      container.classList.add("hidden");
      container.setAttribute("aria-hidden", "true");
    }
  }
  hideContainer(container, hide = true) {
    this.showContainer(container, !hide);
  }
}
